/**
 * File: feedbackService.ts
 * Description: Service for handling feedback API operations
 * Author: System
 * Created: 2025-10-02
 * Last Modified: 2025-10-02
 */

import { apiClient } from './apiClient';

export interface SearchResultFeedbackItem {
  // Core identification fields
  resultId: string;
  originalRank: number;
  newRank: number;
  
  // Feedback data - all other data stored in flexible structure
  feedbackData: FeedbackData;
}

export interface FeedbackData {
  // Core feedback fields
  feedback_type?: 'thumbs_up' | 'thumbs_down' | null;
  comment?: string;
  
  // Common metadata
  timestamp?: string;
  username?: string;
  
  // Flexible structure for any additional data
  [key: string]: any;
}

export interface FeedbackCreateRequest {
  project_id: number;
  section: string;
  agent: string;
  feedback_data: FeedbackData;
}

export interface FeedbackResponse {
  id: number;
  project_id: number;
  section: string;
  agent: string;
  feedback_data: FeedbackData;
  created_at: string;
}

export class FeedbackService {
  private static readonly ENDPOINT = '/api/feedback/';

  /**
   * Submit feedback to the backend
   * @param feedback - Feedback data to submit
   * @returns Promise with the created feedback response
   */
  static async submitFeedback(feedback: FeedbackCreateRequest): Promise<FeedbackResponse> {
    return apiClient.post<FeedbackResponse>(this.ENDPOINT, feedback);
  }

  /**
   * Get feedback list with optional filters
   * @param filters - Optional filters for feedback list
   * @returns Promise with feedback list response
   */
  static async getFeedbackList(filters?: {
    project_id?: number;
    section?: string;
    agent?: string;
    feedback_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ feedback: FeedbackResponse[]; total: number }> {
    const queryParams = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = queryParams.toString() 
      ? `${this.ENDPOINT}?${queryParams.toString()}`
      : this.ENDPOINT;

    return apiClient.get(endpoint);
  }

  /**
   * Delete feedback by ID
   * @param feedbackId - ID of the feedback to delete
   * @returns Promise with success message
   */
  static async deleteFeedback(feedbackId: number): Promise<{ message: string }> {
    return apiClient.delete(`${this.ENDPOINT}/${feedbackId}`);
  }
}

export default FeedbackService;